import React, { useCallback, useState, useRef } from 'react';
import * as RiIcons from 'react-icons/ri';
import { Dialog } from '../Dialog';
import * as Styled from './styles';
import { useMap } from 'hooks/useMap';
import { Modal } from 'components/Modal';
import { Button } from 'components/Button';

import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import KML from 'ol/format/KML';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import shp from 'shpjs';


import { ILayers } from 'contexts/@types/Layers';

interface IUploadLayerDialog {
  open: boolean;
  onClose: () => void;
}

const MAX_FILE_SIZE = 15 * 1024 * 1024; //15MB
const ACCEPTED_EXTENSIONS = ['geojson', 'kml', 'zip']; // zip = shapefile zip

export function UploadLayerDialog({ open, onClose }: IUploadLayerDialog) {
  const { map, handleAddOverlayLayer } = useMap();

  const [file, setFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<'geojson' | 'kml' | 'zip' | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');

  const handleClickDropArea = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    validateAndSetFile(Array.from(e.target.files));
  };

  const showAlert = (message: string) => {
    setAlertMessage(message);
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const getExtension = (filename: string) =>
    filename.split('.').pop()?.toLowerCase() || '';

  const getFileIcon = () => {
    if (!file) return <RiIcons.RiUploadCloud2Line size={42} />;
    if (fileType === 'geojson') return <RiIcons.RiMapLine size={42} />;
    if (fileType === 'kml') return <RiIcons.RiEarthLine size={42} />;
    if (fileType === 'zip') return <RiIcons.RiFileZipLine size={42} />;
    return <RiIcons.RiFile3Line size={42} />;
  };

  const validateAndSetFile = (incoming: File[]) => {
    const f = incoming[0];
    if (!f) return;

    if (f.size > MAX_FILE_SIZE) {
      showAlert('Arquivo excede o limite de 15 MB.');
      return;
    }

    const ext = getExtension(f.name);

    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      showAlert('Tipo de arquivo não suportado. Use GeoJSON, KML ou ZIP (shapefile).');
      return;
    }

    setFile(f);
    setFileType(ext as 'geojson' | 'kml' | 'zip');
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndSetFile(Array.from(e.dataTransfer.files));
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const clearFile = () => {
    setFile(null);
    setFileType(null);
  };

  const handleRemoveLoading = () => {
    setLoading(false);
    setProgress(0);
    setLoadingMessage('');
  }

  const handleAddLayerToMap = async () => {
    if (!map || !file || !fileType) return;

    try {
      let features: any[] = [];
      setLoading(true);
      setProgress(80);
      setLoadingMessage('Carregando camada');

      if (fileType === 'geojson') {
        const text = await file.text();
        const geojson = JSON.parse(text);
        const fmt = new GeoJSON();
        features = fmt.readFeatures(geojson, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        });
      } else if (fileType === 'kml') {
        const text = await file.text();
        const fmt = new KML({ extractStyles: true });
        features = fmt.readFeatures(text, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        });
      } else if (fileType === 'zip') {
        const arrayBuffer = await file.arrayBuffer();
        const geojson: any = await shp(arrayBuffer);
        let fc: any = geojson;
        if (!geojson || !geojson.type) {
          const firstKey = Object.keys(geojson)[0];
          fc = geojson[firstKey];
        }

        if (!fc || !fc.type || fc.type !== 'FeatureCollection') {
          handleRemoveLoading();
          throw new Error('Shapefile inválido ou não retornou GeoJSON FeatureCollection');
        }

        const fmt = new GeoJSON();
        features = fmt.readFeatures(fc, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        });
      }

      if (!features || features.length === 0) {
        handleRemoveLoading();
        showAlert('Nenhuma feature encontrada no arquivo.');
        return;
      }

      const source = new VectorSource({ features });

      const vectorLayer: ILayers = {
        id: Date.now(),
        area_tematica_id: 'upload',
        sub_tematica_id: null,
        order: 0,
        metadados_camadas_id: null,
        layer_order_id: 'upload',
        layer_order: { order: 0 } as any,
        area_tematica: 'Upload',
        layertitle: file.name,
        link_shapefile: '',
        layername: file.name,
        host_url: '',
        metadado_url: '',
        layertype: 'VECTOR',
        sourcetype: 'VECTOR',
        visible: true,
        metadados: {} as any,
        nova_camada: true,
        layer_filter_id: 0,
        layer_query_filter: '',
        atributos: [],
        // flags necessários para o MapProvider
        is_vector: true,
        vector_source: source,
      };

      // usa a função central que adiciona e registra handlers
      handleAddOverlayLayer(vectorLayer);

      // Zoom nas features
      const extent = source.getExtent();
      if (extent && extent.length === 4) {
        map.getView().fit(extent, {
          padding: [40, 40, 40, 40],
          duration: 500,
        });
      }
      clearFile();
      onClose();
    } catch (error: any) {
      handleRemoveLoading();
      console.error(error);
      showAlert('Erro ao carregar arquivo. ' + (error?.message || ''));
    } finally {
      handleRemoveLoading();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Upload de camada">
      <Styled.Container>
        <Styled.DropArea
          isDragging={isDragging}
          hasFile={!!file}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClickDropArea}
          style={{ cursor: 'pointer' }}
        >
          {getFileIcon()}

          {!file && (
            <>
              <span>Arraste e solte o arquivo aqui</span>
              <small>Tipos aceitos: .geojson, .kml e .zip (shapefile)</small>
              <small>Limite máximo: {MAX_FILE_SIZE / (1024 * 1024)} MB</small>
            </>
          )}

          {file && (
            <Styled.FilePreview>
              <Styled.FileName >
                <strong>{file.name}</strong>
              </Styled.FileName>
              <span>{formatFileSize(file.size)}</span>
            </Styled.FilePreview>
          )}

          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            accept=".geojson,.kml,.zip"
            onChange={handleFileChange}
          />

          {loading && (
            <div style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 8 }}>{loadingMessage}</div>
              <Styled.ProgressContainer>
                <Styled.ProgressBar progress={progress} />
              </Styled.ProgressContainer>
            </div>
          )}
        </Styled.DropArea>

        <Styled.Actions>
          <button onClick={clearFile} disabled={!file}>
            Limpar arquivo
          </button>

          <button
            onClick={handleAddLayerToMap}
            disabled={!file || !fileType}
          >
            Adicionar ao mapa
          </button>
        </Styled.Actions>
      </Styled.Container>
      {!!alertMessage && (
        <Modal onDisabled={() => setAlertMessage('')}>
          <div style={{ minWidth: 360, maxWidth: 520 }}>
            <h2 style={{ marginBottom: 12 }}>
              Aviso
            </h2>
            <p style={{ marginBottom: 20 }}>
              {alertMessage}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setAlertMessage('')}
              >
                OK
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Dialog>
  );
}
